import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────
// Guide content — sections by role
// ─────────────────────────────────────────────────────────────
const ALL_SECTIONS = [
  // ── COMMON ──────────────────────────────────────────────────
  {
    id: 'getting-started',
    roles: ['admin','manager','receptionist','boss_secretary','general_user'],
    icon: '🚀',
    title: 'Getting Started',
    items: [
      {
        q: 'How do I log in?',
        a: 'Go to the login page, enter your username and password provided by your administrator. Click Login. You will be taken to your Dashboard.'
      },
      {
        q: 'How do I log out?',
        a: 'Click the red Logout button at the top-right of any page. Your session will end and you will be returned to the login screen.'
      },
      {
        q: 'How do I change my password?',
        a: 'Currently, ask your administrator or manager to reset your password from the User Management page.'
      },
      {
        q: 'Why do I see fewer menu items than expected?',
        a: 'Your administrator may have hidden certain modules for your account. Contact your admin if you need access to a specific section.'
      },
      {
        q: 'What does the Dashboard show?',
        a: 'The Dashboard shows live statistics for all modules in your company. At the top are 4 quick-stat cards: Visitors Inside Now, Vehicles Available, Drivers Available, and Expenses Today. Below are 9 module cards covering Visitors, Meetings, Vehicle Trips, Drivers, Vehicles, Expenses (today + 7-day total), Expenses by Category, Guest Rooms, and Hotel Bookings. Each card shows multiple sub-counts — for example, the Drivers card shows Available / Busy / On Trip / On Leave counts. All figures are filtered to your company automatically.'
      },
    ]
  },

  // ── VISITORS ────────────────────────────────────────────────
  {
    id: 'visitors',
    roles: ['admin','manager','receptionist'],
    icon: '👥',
    title: 'Visitors',
    items: [
      {
        q: 'How do I register a new visitor?',
        a: 'Go to Visitors → click "+ New Visitor". Fill in: Visitor Name (required), Company, Phone, Purpose (required), and the Employee they are here to meet. Click Save. The visitor will appear in today\'s list with status "Inside".'
      },
      {
        q: 'How do I check out a visitor?',
        a: 'Find the visitor in the list and click the "Check Out" button (door icon). Their status changes to "Checked Out" and the departure time is recorded automatically.'
      },
      {
        q: 'How do I search for a visitor?',
        a: 'Use the search box at the top of the Visitors page. You can search by visitor name, company name, or CNIC. Use the date filter to jump to a specific day.'
      },
      {
        q: 'How do I print a visitor badge / QR code?',
        a: 'Click the QR icon next to any visitor. A QR code will appear with visitor details. Use your browser\'s print function (Ctrl+P / Cmd+P) to print.'
      },
      {
        q: 'Can I edit a visitor record after creation?',
        a: 'Yes. Click the ✏️ Edit button next to the visitor. You can update all fields. Only users from the same company can edit a visitor record.'
      },
    ]
  },

  // ── MEETINGS ────────────────────────────────────────────────
  {
    id: 'meetings',
    roles: ['admin','manager','receptionist','boss_secretary'],
    icon: '📅',
    title: 'Meetings',
    items: [
      {
        q: 'How do I schedule a meeting?',
        a: 'Go to Meetings → click "+ New Meeting". Enter: Title, Date, Start Time, End Time. Optionally select a Meeting Room and Host Employee. Click Save. If the room is already booked for that time, you will see a conflict error.'
      },
      {
        q: 'Why doesn\'t my meeting room appear in the dropdown?',
        a: 'Meeting rooms must be created first by an Admin or Manager in the "Meeting Room Setup" page. Only active rooms for your company are shown.'
      },
      {
        q: 'How do I cancel a meeting?',
        a: 'Click the 🗑️ Delete button next to the meeting. The meeting will be marked as "Cancelled" but the record is kept for history.'
      },
      {
        q: 'Can two meetings use the same room at the same time?',
        a: 'No. The system automatically checks for conflicts. If you try to book a room that is already occupied for overlapping times, you will see a "Room already booked" error.'
      },
    ]
  },

  // ── MEETING ROOM SETUP ──────────────────────────────────────
  {
    id: 'meeting-rooms',
    roles: ['admin','manager'],
    icon: '🚪',
    title: 'Meeting Room Setup',
    items: [
      {
        q: 'How do I add a meeting room?',
        a: 'Go to Meeting Room Setup → click "+ Add Room". Enter the room name, capacity, and optional location/description. The room will be available for booking in the Meetings page immediately.'
      },
      {
        q: 'How do I deactivate a room without deleting it?',
        a: 'Edit the room and change its Status to "Inactive". It will no longer appear in the meetings dropdown but historical bookings are preserved.'
      },
    ]
  },

  // ── EMPLOYEES ───────────────────────────────────────────────
  {
    id: 'employees',
    roles: ['admin','manager'],
    icon: '👔',
    title: 'Employees',
    items: [
      {
        q: 'How do I add a single employee?',
        a: 'Go to Employees → click "+ Add Employee". Fill in: Name (required), Department, Designation, Employee Code, Unit, and Phone. Your company is assigned automatically for non-admin users.'
      },
      {
        q: 'How do I bulk import employees from Excel?',
        a: 'Prepare an Excel file with columns: EmployeeCode, FirstName, LastName (or Name), Department, Designation, Phone, Email, Unit. Go to Employees → click "Import Excel". Select your file. Preview the rows and click "Import". Results show how many were imported vs skipped.'
      },
      {
        q: 'What happens to duplicate employee codes?',
        a: 'If an employee code already exists in the active employee list, that row is skipped during import. The error list will show which rows were skipped and why.'
      },
      {
        q: 'How do I deactivate an employee?',
        a: 'Click the 🗑️ Delete button next to the employee. They are soft-deleted (marked inactive) and will no longer appear in dropdowns, but their historical records are preserved.'
      },
    ]
  },

  // ── DRIVERS ─────────────────────────────────────────────────
  {
    id: 'drivers',
    roles: ['admin','manager'],
    icon: '🧑‍✈️',
    title: 'Drivers',
    items: [
      {
        q: 'How do I register a driver?',
        a: 'Go to Drivers → click "+ Add Driver". Enter name, phone, licence number and expiry date. Optionally link to an employee record. Set status to Available. Your company is assigned automatically for non-admin users.'
      },
      {
        q: 'What are the driver status values?',
        a: 'Available — ready to be assigned a trip. Busy — assigned to a planned trip that has not started yet. On Trip — currently on a started trip. On Leave — manually set by a manager/admin when the driver is away. Inactive — deactivated driver, cannot be assigned.'
      },
      {
        q: 'Why is a driver shown as "Busy"?',
        a: 'When a new vehicle trip is created (planned but not yet started), the assigned driver is automatically set to Busy. This prevents double-booking. When the trip is started, the driver moves to On Trip. When the trip is completed or cancelled, they return to Available automatically.'
      },
      {
        q: 'Why is a driver shown as "On Trip"?',
        a: 'When a planned trip is started using the Start button in Vehicle Trips, the driver status changes from Busy to On Trip. This means they are currently travelling. They return to Available only when the trip is completed or cancelled.'
      },
      {
        q: 'How do I put a driver on leave?',
        a: 'Click ✏️ Edit on the driver. The Status dropdown shows: Available, On Leave, Inactive. Select On Leave and click Update. The driver will not appear in the available drivers list and cannot be assigned to a new trip while on leave.'
      },
      {
        q: 'Why can\'t I change a driver\'s status while they are Busy or On Trip?',
        a: 'When a driver has an active trip (Busy or On Trip), the status field in the edit form is locked with a message: "complete or cancel the trip to change". This prevents accidental status changes that would cause data inconsistency. Go to Vehicle Trips, complete or cancel the trip first, and the lock will be released automatically.'
      },
      {
        q: 'Why does a driver not appear in the dropdown when creating a trip?',
        a: 'Only drivers with status "Available" appear in the trip creation dropdown. If a driver is Busy, On Trip, On Leave, or Inactive, they will not be listed. Set the driver to Available first, or choose a different driver.'
      },
      {
        q: 'What are the status summary pills on the Drivers page?',
        a: 'At the top of the Drivers page, coloured pills show a count breakdown — e.g. "Available: 3  Busy: 1  On Trip: 2". These give a quick overview of fleet readiness without having to count rows manually.'
      },
    ]
  },

  // ── GUEST HOUSES ────────────────────────────────────────────
  {
    id: 'guest-houses',
    roles: ['admin','manager'],
    icon: '🏠',
    title: 'Guest Houses',
    items: [
      {
        q: 'How do I set up a guest house?',
        a: 'Go to Guest Houses → click "+ Add Guest House". Enter the name, city, address, and optional unit linkage. After saving, open the guest house and use the Rooms tab to add individual rooms.'
      },
      {
        q: 'How do I add rooms to a guest house?',
        a: 'Open a guest house record → click "+ Add Room". Enter room name/number, room type (single/double/suite/etc.), capacity, and floor. Click Save.'
      },
      {
        q: 'How do I disable a room without deleting it?',
        a: 'Edit the room and uncheck the "Active" toggle. The room will no longer appear for booking but its history is preserved.'
      },
    ]
  },

  // ── GUEST ROOMS ─────────────────────────────────────────────
  {
    id: 'guest-rooms',
    roles: ['admin','manager','receptionist','boss_secretary'],
    icon: '🏨',
    title: 'Guest Room Bookings',
    items: [
      {
        q: 'How do I book a guest room?',
        a: 'Go to Guest Rooms → click "+ New Booking". Select the Room (only rooms from your company are shown), enter guest name, check-in and check-out dates. Click Save.'
      },
      {
        q: 'How do I check in or check out a guest?',
        a: 'Find the booking and click "Check In" to mark the guest as arrived, or "Check Out" to mark them as departed. These buttons appear based on the current booking status.'
      },
      {
        q: 'Can I link a booking to an employee or visitor?',
        a: 'Yes. In the booking form, you can optionally link to an existing Employee or Visitor record for tracking purposes.'
      },
    ]
  },

  // ── HOTEL BOOKINGS ──────────────────────────────────────────
  {
    id: 'hotel-bookings',
    roles: ['admin','manager','receptionist','boss_secretary'],
    icon: '🏩',
    title: 'Hotel Bookings',
    items: [
      {
        q: 'How do I record an external hotel booking?',
        a: 'Go to Hotel Bookings → click "+ New Booking". Enter guest name, city, hotel name, room number, check-in/out dates, and charges. Optionally link to an employee. Click Save.'
      },
      {
        q: 'How do I add a new hotel to the system?',
        a: 'In the Hotel Bookings page, click "Manage Hotels" → "+ Add Hotel". Enter hotel name, city, address and phone. Hotels are shared across all companies.'
      },
    ]
  },

  // ── VEHICLES ────────────────────────────────────────────────
  {
    id: 'vehicles',
    roles: ['admin','manager'],
    icon: '🚗',
    title: 'Vehicles',
    items: [
      {
        q: 'How do I register a vehicle?',
        a: 'Go to Vehicles → click "+ Add Vehicle". Enter vehicle number (required), type, brand/model, registration number, fuel type, and current odometer reading. Your company is assigned automatically.'
      },
      {
        q: 'What are the vehicle status values?',
        a: 'Available — ready to be assigned a trip. Busy — assigned to a planned trip that has not started yet. On Trip — currently on a started trip. Maintenance — under repair, manually set. Inactive — deactivated vehicle.'
      },
      {
        q: 'Why is a vehicle shown as "Busy (Planned)"?',
        a: 'When a new vehicle trip is created but not yet started, the vehicle status changes to Busy. This indicates it is reserved for a planned trip. The vehicle will not appear in the available vehicles list during this time.'
      },
      {
        q: 'Why is a vehicle shown as "On Trip"?',
        a: 'When a planned trip is started (using the Start button in Vehicle Trips), the vehicle moves from Busy to On Trip. This means it is currently travelling. It returns to Available only when the trip is completed or cancelled.'
      },
      {
        q: 'Why is the status dropdown locked on a vehicle card?',
        a: 'When a vehicle has an active trip (status is Busy or On Trip), the quick-status dropdown on the vehicle card is replaced with a lock message: "complete/cancel trip to change". Go to Vehicle Trips, complete or cancel the trip first to unlock it.'
      },
      {
        q: 'How do I mark a vehicle under maintenance?',
        a: 'On the vehicle card, use the status dropdown (only visible when the vehicle is Available or Inactive — not locked). Select "Maintenance". The vehicle will not appear in the available vehicles list and cannot be assigned to any new trip.'
      },
      {
        q: 'How do I filter vehicles by status?',
        a: 'At the top of the Vehicles page, click the filter buttons: All, Available, Busy, On Trip, Maintenance, or Inactive. Each button shows the count in brackets. Click a button to see only vehicles with that status.'
      },
    ]
  },

  // ── VEHICLE TRIPS ───────────────────────────────────────────
  {
    id: 'trips',
    roles: ['admin','manager','receptionist'],
    icon: '📍',
    title: 'Vehicle Trips',
    items: [
      {
        q: 'How do I record a vehicle trip?',
        a: 'Go to Vehicle Trips → click "+ New Trip". Select the Vehicle and Driver (only Available vehicles and drivers are shown), enter the trip date, from/to locations, purpose, and starting odometer. Click Save. The vehicle and driver are immediately set to "Busy (Planned)" — they are reserved and cannot be assigned elsewhere.'
      },
      {
        q: 'How do I start a trip?',
        a: 'Find the planned trip in the list and click the "Start" button. You can confirm or update the starting odometer and departure time. Once started, the vehicle and driver status changes from "Busy" to "On Trip". Active trips appear at the top of the list.'
      },
      {
        q: 'How do I complete a trip?',
        a: 'Find the started trip and click "Complete". Enter the ending odometer, end time, and any expenses (fuel cost, toll, parking, driver allowance). Click Save. The vehicle and driver automatically return to "Available". Fuel and trip expenses are auto-created if amounts are entered.'
      },
      {
        q: 'Can I cancel a trip?',
        a: 'Yes. Click the 🗑️ Cancel button on any planned or started trip. The trip is marked cancelled and the vehicle and driver are immediately returned to "Available" status.'
      },
      {
        q: 'Why can\'t I assign a driver to a new trip?',
        a: 'The driver may be Busy (on a planned trip), On Trip (trip started), On Leave, or Inactive. Only drivers with status "Available" appear in the dropdown. Check the Drivers page to see the current status, and complete/cancel any existing trip or change the driver\'s status first.'
      },
    ]
  },

  // ── FUEL EXPENSES ───────────────────────────────────────────
  {
    id: 'fuel',
    roles: ['admin','manager','receptionist'],
    icon: '⛽',
    title: 'Fuel Expenses',
    items: [
      {
        q: 'How do I log a fuel expense?',
        a: 'Go to Fuel Expenses → click "+ Add Expense". Select the vehicle, enter the date, quantity (litres), cost (PKR), fuel station name, and current odometer reading. Click Save. The vehicle odometer is updated automatically.'
      },
      {
        q: 'How do I view fuel expenses by vehicle?',
        a: 'Use the Vehicle filter at the top of the Fuel Expenses page to filter by a specific vehicle. The Summary section shows totals grouped by vehicle.'
      },
    ]
  },

  // ── TCS COURIER ─────────────────────────────────────────────
  {
    id: 'couriers',
    roles: ['admin','manager','receptionist'],
    icon: '📦',
    title: 'TCS Courier',
    items: [
      {
        q: 'How do I log a courier dispatch?',
        a: 'Go to TCS Courier → click "+ New Courier". Select the Courier Company (TCS, DHL, etc.), enter the date, sender employee, receiver details (city, company, person), tracking number, weight and charges. Click Save.'
      },
      {
        q: 'How do I update delivery status?',
        a: 'Find the courier record and click ✏️ Edit. Change the Delivery Status from "Pending" to "Delivered" or "Returned". Click Save.'
      },
      {
        q: 'How do I add a courier company (TCS, DHL, etc.)?',
        a: 'This is done from the Courier Companies page (Admin menu). Click "+ Add Company" and enter the name and type.'
      },
    ]
  },

  // ── INTERNAL COURIER ────────────────────────────────────────
  {
    id: 'internal-courier',
    roles: ['admin','manager','receptionist'],
    icon: '🔄',
    title: 'Internal Courier',
    items: [
      {
        q: 'How do I record an internal dispatch?',
        a: 'Go to Internal Courier → click "+ New Dispatch". Select From Unit, To Unit, dispatch date, and the employee sending it. Optionally assign a vehicle. Enter any van rent, fuel, or miscellaneous expenses. Click Save.'
      },
      {
        q: 'How do I mark a dispatch as delivered?',
        a: 'Edit the dispatch record and change Status to "Delivered". Click Save.'
      },
    ]
  },

  // ── EXPENSES ────────────────────────────────────────────────
  {
    id: 'expenses',
    roles: ['admin','manager','receptionist'],
    icon: '💰',
    title: 'Expenses',
    items: [
      {
        q: 'How do I record an expense?',
        a: 'Go to Expenses → click "+ New Expense". Select the Category: Visitor, Guest House, Employee, Vehicle, Courier, or Other. Enter the amount, date, and description. Optionally link to an employee, visitor, or booking. Click Save.'
      },
      {
        q: 'What are the expense categories?',
        a: 'Visitor — expenses incurred for a specific visitor. Guest House — room/accommodation costs. Employee — employee-related expenses. Vehicle — vehicle maintenance, toll, etc. Courier — courier charges. Other — anything else.'
      },
      {
        q: 'Can I filter expenses by date range?',
        a: 'Yes. Use the From and To date filters at the top of the Expenses page. You can also filter by category, company (admin only), department, and employee.'
      },
    ]
  },

  // ── ADDRESS BOOK ────────────────────────────────────────────
  {
    id: 'address-book',
    roles: ['admin','manager','receptionist','boss_secretary','general_user'],
    icon: '📒',
    title: 'Address Book',
    items: [
      {
        q: 'How do I add a contact?',
        a: 'Go to Address Book → click "+ New Contact". Enter Name (required), CNIC, Phone, Email, Address, City, and Company Name. Click Save. Your contact is private — no other user can see it.'
      },
      {
        q: 'Can other users see my contacts?',
        a: 'No. The Address Book is fully private. Each user sees only their own contacts. Even users in the same company or admins cannot view your personal contacts.'
      },
      {
        q: 'How do I search my contacts?',
        a: 'Use the search box at the top of the Address Book page. It searches by name, phone number, company name, and city simultaneously.'
      },
      {
        q: 'How do I import contacts in bulk?',
        a: 'Click "Import CSV" and download the template first. Fill in the CSV with your contacts and upload it. Results show how many contacts were imported.'
      },
      {
        q: 'How do I export my contacts?',
        a: 'Click "Export CSV" at the top of the Address Book page. A CSV file with all your active contacts will be downloaded.'
      },
    ]
  },

  // ── REPORTS ─────────────────────────────────────────────────
  {
    id: 'reports',
    roles: ['admin','manager','boss_secretary'],
    icon: '📈',
    title: 'Reports',
    items: [
      {
        q: 'What reports are available?',
        a: 'Daily Visitors, Monthly Visitors, Visitors by Company, Audit Visits, Expenses, Expenses by Company, Couriers, Guest Room Bookings, Employees List, and Meetings.'
      },
      {
        q: 'How do I generate a report?',
        a: 'Go to Reports → select the report type from the tabs. Fill in the filter fields (dates, company, department) and click "Run Report". The results appear below.'
      },
      {
        q: 'Can I export a report to Excel?',
        a: 'Yes. After running a report, click the "Export Excel" button. The data will be downloaded as an Excel file.'
      },
      {
        q: 'Can I export to CSV?',
        a: 'Yes. Click "Export CSV" for a comma-separated file compatible with any spreadsheet application.'
      },
      {
        q: 'Can I filter reports by department or unit?',
        a: 'Yes. Most reports have optional Department and Unit filters. Select a company first (admin only), then department and unit dropdowns are also available.'
      },
    ]
  },

  // ── ALERTS ──────────────────────────────────────────────────
  {
    id: 'alerts',
    roles: ['admin','manager','receptionist','boss_secretary','general_user'],
    icon: '🔔',
    title: 'Alerts',
    items: [
      {
        q: 'What are alerts?',
        a: 'Alerts are system notifications generated automatically when key events occur, such as a new visitor arrival. They appear on the Alerts page and the 🔔 bell icon in the header.'
      },
      {
        q: 'How do I mark an alert as read?',
        a: 'Open the Alerts page and click the alert to mark it as read, or use the "Mark All Read" button.'
      },
    ]
  },

  // ── USER MANAGEMENT ─────────────────────────────────────────
  {
    id: 'users',
    roles: ['admin','manager'],
    icon: '⚙️',
    title: 'User Management',
    items: [
      {
        q: 'How do I create a new user?',
        a: 'Go to User Management → click "+ Create User". Enter username (unique), password, full name, and role. Optionally link to an employee record. Managers can only create users for their own company and cannot assign the Admin role.'
      },
      {
        q: 'What roles are available?',
        a: 'Admin (full access, all companies), Manager (company-wide access, can manage users/vehicles/drivers), Receptionist (day-to-day operations), Boss Secretary (meetings, hotel, address book), General User (address book only by default).'
      },
      {
        q: 'How do I reset a user\'s password?',
        a: 'Find the user → click "🔑 Reset" button. Enter and confirm the new password. Click Save. The user will need to log out and back in.'
      },
      {
        q: 'How do I deactivate a user?',
        a: 'Edit the user and toggle the Active switch to off. The user will not be able to log in until reactivated.'
      },
      {
        q: 'Why can\'t a user see a particular page?',
        a: 'Check Access Management (admin only). The feature may have "Hidden" checked for that user. Uncheck it, save, and ask the user to log out and back in.'
      },
    ]
  },

  // ── ACCESS MANAGEMENT ───────────────────────────────────────
  {
    id: 'access-management',
    roles: ['admin'],
    icon: '🔐',
    title: 'Access Management',
    items: [
      {
        q: 'How do I hide a module from a specific user?',
        a: 'Go to Access Management → select the user from the left panel → find the module row → check the "Hidden" checkbox → click "Save Permissions". The user must log out and back in for the change to take effect.'
      },
      {
        q: 'What is the "Hidden" checkbox?',
        a: 'When Hidden is checked, the sidebar link for that module disappears for that user. They also cannot navigate directly to the page URL.'
      },
      {
        q: 'What do Read and Write checkboxes do?',
        a: 'These are stored for future use and for your own record-keeping. The current system uses the "Hidden" flag for sidebar visibility control.'
      },
      {
        q: 'What is Company Access?',
        a: 'Company Access is only relevant for Admin-role users. It allows you to grant specific admins cross-company read/write access. Regular users (manager, receptionist, etc.) are always scoped to their own company via their JWT token and do not use these settings.'
      },
      {
        q: 'General users see all modules after login — is that a bug?',
        a: 'No. New general users see only the Address Book by default. If you grant them additional access in Access Management and save, they will see those modules after re-login.'
      },
    ]
  },

  // ── BACKUP & RESTORE ────────────────────────────────────────
  {
    id: 'backup',
    roles: ['admin'],
    icon: '💾',
    title: 'Backup & Restore',
    items: [
      {
        q: 'How do I create a manual backup?',
        a: 'Go to Backup & Restore → click "Create Backup". The system runs pg_dump and saves the SQL file to the server. The backup appears in the Files list. Click "Download" to save a copy to your computer.'
      },
      {
        q: 'How do I restore from a backup?',
        a: 'Go to Backup & Restore → Restore tab → click "Choose File" and select a .sql backup file → click "Restore Database". WARNING: This will apply the SQL on top of your existing database. Always download a fresh backup before restoring.'
      },
      {
        q: 'How often are automatic backups taken?',
        a: 'Automatic backups are not currently scheduled by default. You can set one up using cron on the VPS or configure the Google Drive Backup feature for automated daily backups.'
      },
      {
        q: 'CRITICAL: Never run docker compose down -v',
        a: 'The -v flag destroys the database volume and ALL data permanently. Always use: docker compose down (without -v) to stop containers safely.'
      },
    ]
  },

  // ── COMPANIES & UNITS ───────────────────────────────────────
  {
    id: 'companies-units',
    roles: ['admin'],
    icon: '🏢',
    title: 'Companies & Units',
    items: [
      {
        q: 'How do I add a new company?',
        a: 'Go to Companies → click "+ Add Company". Enter company name (required), company code (required, used for data isolation), short name (monogram shown in sidebar when no logo — e.g. STM), address and contact. Once created, assign the brand colors and users via User Management.'
      },
      {
        q: 'How do I set a company\'s brand colors and logo?',
        a: 'Go to Companies → click ✏️ Edit on the company. In the "Brand Colors" section you will find 6 color pickers: Primary Color (top of sidebar gradient), Secondary Color (bottom of gradient), Accent Color (general highlights), Sidebar Color, Header Color, and Icon Color. You can also click a palette preset to apply a full color scheme at once. A live sidebar preview shows the result in real time. For a logo, enter the image URL in the Logo URL field — paste a direct image link (e.g. from your server or CDN). Click Update to save.'
      },
      {
        q: 'When do users see the new company branding?',
        a: 'Branding is loaded at login time and stored in the browser session. After you update company colors, all users of that company must log out and log back in to see the new colors applied to the sidebar and header.'
      },
      {
        q: 'What is the Short Name field?',
        a: 'The Short Name (max 10 characters) is used as a monogram badge in the sidebar when no logo URL is set. For example, "STM" for Shahzad Textile Mills or "SJM" for Sargodha Jute Mills. It appears in a colored circle in the top-left of the sidebar.'
      },
      {
        q: 'How do I add a unit?',
        a: 'Go to Units → click "+ Add Unit". Enter the unit name and optionally link it to a company. Units are used to organise employees, guest houses and expenses.'
      },
    ]
  },
];

// ─────────────────────────────────────────────────────────────
const ROLE_LABELS = {
  admin: 'Administrator',
  manager: 'Manager',
  receptionist: 'Receptionist',
  boss_secretary: 'Boss Secretary',
  general_user: 'General User',
};

export default function UserGuide() {
  const { user } = useAuth();
  const userRole = user?.role || 'general_user';

  const [search, setSearch] = useState('');
  const [openSection, setOpenSection] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [roleFilter, setRoleFilter] = useState('mine'); // 'mine' | 'all'

  const visibleSections = useMemo(() => {
    let sections = roleFilter === 'mine'
      ? ALL_SECTIONS.filter(s => s.roles.includes(userRole))
      : ALL_SECTIONS;

    if (search.trim()) {
      const q = search.toLowerCase();
      sections = sections
        .map(s => ({
          ...s,
          items: s.items.filter(
            i => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q)
          ),
        }))
        .filter(s => s.items.length > 0 || s.title.toLowerCase().includes(q));
    }
    return sections;
  }, [search, roleFilter, userRole]);

  const toggleSection = (id) => {
    setOpenSection(prev => prev === id ? null : id);
    setOpenItem(null);
  };

  const toggleItem = (key) => {
    setOpenItem(prev => prev === key ? null : key);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">User Guide</h1>
        <p className="text-gray-500 text-sm mt-1">
          Step-by-step help for the Reception Management System
          {userRole && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              {ROLE_LABELS[userRole] || userRole}
            </span>
          )}
        </p>
      </div>

      {/* Search + filter bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
          <input
            type="text"
            placeholder="Search help topics..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">✕</button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setRoleFilter('mine')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              roleFilter === 'mine'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            My Topics
          </button>
          <button
            onClick={() => setRoleFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              roleFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Topics
          </button>
        </div>
      </div>

      {/* Sections */}
      {visibleSections.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-400">
          <div className="text-4xl mb-2">🔍</div>
          <p>No results found for "<strong>{search}</strong>"</p>
          <button onClick={() => setSearch('')} className="mt-3 text-blue-600 text-sm underline">Clear search</button>
        </div>
      ) : (
        visibleSections.map(section => (
          <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Section header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{section.icon}</span>
                <div>
                  <span className="font-semibold text-gray-800">{section.title}</span>
                  <span className="ml-3 text-xs text-gray-400">{section.items.length} topics</span>
                </div>
              </div>
              <span className="text-gray-400 text-lg">{openSection === section.id ? '▲' : '▼'}</span>
            </button>

            {/* FAQ items */}
            {openSection === section.id && (
              <div className="border-t border-gray-100 divide-y divide-gray-50">
                {section.items.map((item, idx) => {
                  const key = `${section.id}-${idx}`;
                  return (
                    <div key={key}>
                      <button
                        onClick={() => toggleItem(key)}
                        className="w-full flex items-start justify-between px-5 py-4 text-left hover:bg-blue-50 transition-colors gap-4"
                      >
                        <span className="text-sm font-medium text-gray-700 flex-1">{item.q}</span>
                        <span className="text-gray-400 text-xs flex-shrink-0 mt-0.5">
                          {openItem === key ? '▲' : '▼'}
                        </span>
                      </button>
                      {openItem === key && (
                        <div className="px-5 pb-4">
                          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-gray-700 leading-relaxed">
                            {item.a}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}

      {/* Footer */}
      <div className="text-center text-xs text-gray-400 pb-4">
        Reception Management System — Help Guide
        {user?.company_name && <span className="ml-1">· {user.company_name}</span>}
      </div>
    </div>
  );
}
